import { useEffect, useRef, useState } from "react";
import { Compass, MapPin } from "lucide-react";
import { useTranslation } from "../../i18n/LanguageContext";

const HERO_IMAGE =
  "https://images.pexels.com/photos/18806130/pexels-photo-18806130.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function Hero({ onQuoteClick }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 900) setOffset(y * 0.25);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="fooldal"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      <div
        className="absolute inset-0 will-change-transform"
        style={{
          transform: `translateY(${offset}px) scale(1.05)`,
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 hero-vignette" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#83588f] opacity-[0.10] blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pt-36 sm:pt-44 md:pt-56 pb-24 md:pb-40 flex flex-col items-start min-h-screen justify-center">
        <div className="max-w-4xl">
          <p className="fade-up whitespace-nowrap text-[10px] sm:text-xs tracking-[0.22em] sm:tracking-[0.32em] uppercase text-[#a88644] font-semibold" data-testid="hero-eyebrow">
            {t.hero.eyebrow}
          </p>
          <h1
            data-testid="hero-title"
            className="mt-6 sm:mt-8 font-serif-display text-[40px] leading-[1.05] sm:text-5xl md:text-7xl lg:text-[88px] sm:leading-[1.02] tracking-tight text-[#0a0a0a] fade-up fade-up-d1"
          >
            {t.hero.titleA}{" "}
            <span className="italic font-normal gold-text hero-gold-accent">
              {t.hero.titleB}
            </span>
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mt-6 md:mt-8 text-base md:text-xl text-[#594f5a] leading-relaxed max-w-2xl fade-up fade-up-d2"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-8 md:mt-12 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 fade-up fade-up-d3">
            <a
              href="#kirandulasok"
              data-testid="hero-cta-trips"
              className="btn-gold px-6 sm:px-7 py-3.5 sm:py-4 rounded-full text-sm inline-flex items-center justify-center gap-2"
            >
              {t.hero.ctaTrips}
              <Compass size={16} />
            </a>
            <button
              data-testid="hero-cta-quote"
              onClick={onQuoteClick}
              className="btn-outline-cream px-6 sm:px-7 py-3.5 sm:py-4 rounded-full text-sm"
            >
              {t.hero.ctaQuote}
            </button>
          </div>

          <div className="mt-10 sm:mt-14 inline-flex items-center gap-3 fade-up fade-up-d4">
            <span className="w-9 h-9 rounded-full border border-[#0a0a0a]/40 flex items-center justify-center bg-[#ebe2f5]/60 shrink-0">
              <MapPin size={14} className="text-[#0a0a0a]" strokeWidth={1.5} />
            </span>
            <div className="leading-tight">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#c5a059]">
                {t.hero.hqLabel}
              </div>
              <div className="text-sm text-[#1a1a1a] mt-0.5">
                {t.hero.hqValue}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#dfd3ee] pointer-events-none" />
    </section>
  );
}
